<template>
	<row-object-page name="mail_account_edit" :oref="oref" v-model="mailAccount" :buttons="buttons" :parent-errors="errors" :add-save="false" :add-save-and-back="false"></row-object-page>
</template>

<script setup lang="ts">
import {ref, computed, onUnmounted, reactive} from "vue";
import RowObjectPage from "@/pages/row-object/RowObjectPage.vue";
import {objectRef, RowObjectInit, RowObjectRef, RowObjectType, SrcType} from "@t/meta/SourceType.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {TOOLS_GROUP_ID} from "@t/ui/Menu.ts";
import type {MailAccount} from "@t/mail/MailAccount.ts";
import {connectEventBus, injectEnv, userInRole} from "@fd/Environment.ts";
import {Button} from "@t/fd/fixed/Button.ts";
import {go} from "@/router/Router.ts";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {useVirtualRoute} from "@/router/virtual/VirtualRouter.ts";
import {FormPartOrReference} from "@t/fd/FormPartOrReference.ts";
import {CREATE, EDIT, REPLACE_INIT_WITH_EDIT} from "@/pages/row-object/RowObjectEvents.ts";
import {NetworkResponse} from "@fd/Types.ts";
import {WithMeta} from "@t/fd/WithMeta.ts";
import {ApiError} from "@t/util/ApiError.ts";

const ns = useNavigationStore();
const env = injectEnv();
const bus = connectEventBus("MailAccountPage");
const errors = reactive<ErrorMap>([]);

const mailAccount = ref<MailAccount | null>(null);

const route = useVirtualRoute();
const oref = objectRef(route, SrcType.MAIL_ACCOUNT);

ns.listenForStackerUpdates(bus, oref, () => env.msg('menu.createMailAccount'),
	"/editMailAccount.do", () => mailAccount.value.name, () => mailAccount.value.id);

const buttons = computed<FormPartOrReference[]>(() => {
	if (mailAccount.value && mailAccount.value.configStep<2) {
		return  [<Button>{ type: "button", label: env.msg('butt.continue'), action: "initdata", feedback: true }];
	} else if (mailAccount.value && (!mailAccount.value.id || mailAccount.value.id===0)
				&& mailAccount.value.accountType!=='POP3') {
		return  ['continue'];
	} else {
		return  ['save','saveAndBack'];
	}
});

ns.defineMenu((mb) => {
	if (mailAccount.value?.id) {
		mb.inGroup(TOOLS_GROUP_ID, (gb) => {
			if (mailAccount.value.accountType==='POP3') {
				gb.navItem("unlimitedMailCheck",
					"unlimitedMailCheck.do?account="+mailAccount.value.id,
					"menu.mailAccount.unlimitedMailCheck", "radar");
			}
			else if (mailAccount.value.accountType==='IMAP') {
				gb.navItem("editFolderMappings",
					"editFolderMappings.do?id="+mailAccount.value.id,
					"menu.editIMAPFolders", "empty-box");
			}
			else if (mailAccount.value.accountType==='GMAIL' || mailAccount.value.accountType==='OFFICE365') {
				gb.navItem("refreshAccount",
					"syncWizard.do?step=refresh&mailAccount="+mailAccount.value.id,
					"mailaccount.oauth.enable", "wrench");
			}

			if (mailAccount.value.accountType==='GMAIL') {
				gb.navItem("unlockCaptcha",
					"https://www.google.com/accounts/DisplayUnlockCaptcha",
					"syncAccount.unlockCaptcha", "wrench", false, "_blank");
			}

			if (mailAccount.value.accountType==='IMAP') {
				gb.navItem("changeToGmail",
					"changeMailAccountType.do?accountType=GMAIL&mailAccount="+mailAccount.value.id,
					"mailaccount.oauth.changeType.gmail", "wrench", false, null, "confirm.changeMailAccountType");

				gb.navItem("changeToOffice",
					"changeMailAccountType.do?accountType=OFFICE365&mailAccount="+mailAccount.value.id,
					"mailaccount.oauth.changeType.office365", "wrench", false, null, "confirm.changeMailAccountType");

				if (userInRole(env.user, "admin.app")) {
					gb.navItem("resyncIMAPAccount",
						"resyncIMAPAccount.do?reSync=true&account="+mailAccount.value.id,
						"menu.resyncIMAPAccount", "refresh", false, null, "menu.resyncIMAPAccount.confirm");
				}

				gb.navItem("fullSyncIMAPAccount",
					"resyncIMAPAccount.do?fullSync=true&account="+mailAccount.value.id,
					"menu.fullsyncIMAPAccount", "wrench", false, null, "menu.resyncIMAPAccount.confirm");
			}
		});
	}
});

function initData():Promise<NetworkResponse> {
	errors.length = 0;
    return env.net.fetch("api/mailaccounts/init", "POST", mailAccount.value);
}

function receivedData(data:MailAccount & WithMeta):void {
	if (data.META) {
		delete data.META;
	}
	mailAccount.value = data;
	checkGlobalErrors();
}

function checkGlobalErrors() {
	if (mailAccount.value?.lastError) {
		let problem:ApiError = { type:"ERROR", level:"SEVERE", message: env.msg('err.mailAccount.errorWarning',mailAccount.value.lastError) };
		errors.push(problem);
	}
	
	let msgValue = null;
	if (mailAccount.value?.accountType==='OFFICE365' && mailAccount.value.emptyMicrosoftApiSetting) {// && mailAccount.value.configStep===2 
		msgValue =  env.msg('err.mailAccount.edit.emptyMicrosoftSetting')+" "
				+env.msg('err.mailAccount.edit.emptyMicrosoftSetting'+(userInRole(env.user, 'admin.app')?'.admin':'.user'));		
	}
	else if (mailAccount.value?.accountType==='GMAIL' && mailAccount.value.emptyGoogleApiSetting) {// && mailAccount.value.configStep===2 
		msgValue = env.msg('err.mailAccount.edit.emptyGoogleSetting')+" "
				+env.msg('err.mailAccount.edit.emptyGoogleSetting'+(userInRole(env.user, 'admin.app')?'.admin':'.user'));	
	}
	if (msgValue) {
		errors.push(<ApiError>{ type:"ERROR", level:"WARNING", html: true, message: msgValue });	
	}
}

function doEdit(oref:RowObjectRef) {
	if (oref.type === SrcType.MAIL_ACCOUNT) {
		if (mailAccount.value?.globalSmtp) {
			console.log("is globalSmtp");
			let problem = <ApiError>{ type:"ERROR", level:"WARNING", message: env.msg('warning.globalSmtp.info') };
			errors.push(problem);
		}
	}
}
bus.on(EDIT, doEdit);

bus.onCommand(["initdata", "continue"], (cmd:string) => {
	if (cmd === "initdata") {
		return initData()
			.then((response)=>response.json())
			.then((data)=>{
				receivedData(data);
				mailAccount.value.configStep=2;
				return "ok";
			});
	} else {
		return new Promise(async (resolve) => {
			bus.executeCommand("saveOnly").then((success)=>{
				if (success) {//if save is o.k.
					if (cmd === "continue") {
						let link = null;
						if (mailAccount.value?.accountType==='IMAP') {
							link = "./editFolderMappings.do?id="+mailAccount.value.id;
						}
						else if (mailAccount.value?.accountType==='GMAIL' || mailAccount.value?.accountType==='OFFICE365') {
							link = "./syncWizard.do?step=refresh&mailAccount="+mailAccount.value.id;
						}
						//console.log("link:"+link);
						go(link);
					}
					resolve("ok");
				}
				else {
					resolve("fail");
				}
			})
			.catch((error) => {
				console.error("Error, stop nex step:", error);
			});
		});
	}
});

</script>

<style>
</style>
